import { createFileRoute } from '@tanstack/react-router';
import { Server, Router, RefreshCw } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable } from '@/components/common/DataTable';
import { Mono } from '@/components/common/display';
import { NotImplementedState, LoadingRows } from '@/components/common/Feedback';
import { Button } from '@/components/ui/button';
import { formatRelative } from '@/lib/format';
import { useStubResource, isNotImplemented } from '@/hooks/use-resource';

interface DeviceItem {
  id: string;
  hostname: string;
  vendor: string;
  mgmt_address: string;
  last_sync: string;
}

type VendorKey = 'mikrotik' | 'juniper' | 'cisco' | 'arista' | 'huawei';

const VENDOR_COLOR: Record<VendorKey, string> = {
  mikrotik: '#ef4444',
  juniper: '#3b82f6',
  cisco: '#22c55e',
  arista: '#a855f7',
  huawei: '#f97316',
};

const VENDOR_LABEL: Record<VendorKey, string> = {
  mikrotik: 'MikroTik',
  juniper: 'Juniper',
  cisco: 'Cisco',
  arista: 'Arista',
  huawei: 'Huawei',
};

function VendorCell({ vendor }: { vendor: string }) {
  const key = vendor.toLowerCase() as VendorKey;
  const color = VENDOR_COLOR[key] ?? 'var(--fg-muted)';
  const label = VENDOR_LABEL[key] ?? vendor;
  const Icon = key === 'juniper' || key === 'cisco' || key === 'arista' ? Router : Server;

  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span
        className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm"
        style={{ backgroundColor: `${color}22`, color }}
      >
        <Icon className="size-3" />
      </span>
      {label}
    </span>
  );
}

function SyncButton({ deviceId }: { deviceId: string }) {
  function handleSync() {
    // Void the parameter to satisfy no-unused-locals; backend stub not implemented
    void deviceId;
    toast.message('Sync no implementado en el backend');
  }

  return (
    <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-[10px]" onClick={handleSync}>
      <RefreshCw className="size-2.5" />
      Sync
    </Button>
  );
}

const COLUMNS: ColumnDef<DeviceItem>[] = [
  {
    accessorKey: 'hostname',
    header: 'Hostname',
    cell: ({ row }) => <Mono className="text-xs font-medium">{row.original.hostname}</Mono>,
  },
  {
    accessorKey: 'vendor',
    header: 'Fabricante',
    cell: ({ row }) => <VendorCell vendor={row.original.vendor} />,
  },
  {
    accessorKey: 'mgmt_address',
    header: 'Mgmt IP',
    cell: ({ row }) =>
      row.original.mgmt_address ? (
        <Mono className="text-xs">{row.original.mgmt_address}</Mono>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    accessorKey: 'last_sync',
    header: 'Último sync',
    cell: ({ row }) =>
      row.original.last_sync ? (
        <span className="text-xs text-muted-foreground">{formatRelative(row.original.last_sync)}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'actions',
    header: '',
    enableHiding: false,
    cell: ({ row }) => <SyncButton deviceId={row.original.id} />,
  },
];

export const Route = createFileRoute('/_auth/devices')({
  component: DevicesPage,
});

function DevicesPage() {
  const { data, isLoading, error } = useStubResource<DeviceItem>('devices');

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Dispositivos"
        icon={Server}
        description="Routers, switches y equipos de red destino de sincronización"
      />

      <div className="flex min-h-0 flex-1 flex-col p-4">
        {isLoading ? (
          <div className="space-y-2 p-2">
            <LoadingRows rows={8} />
          </div>
        ) : isNotImplemented(error) ? (
          <NotImplementedState resource="Dispositivos" />
        ) : (
          <DataTable
            columns={COLUMNS}
            data={data?.data ?? []}
            getRowId={(r) => r.id}
            error={!isNotImplemented(error) ? error : undefined}
            emptyTitle="Sin dispositivos"
            emptyDescription="No hay dispositivos registrados."
            csvFilename="devices"
          />
        )}
      </div>
    </div>
  );
}
